import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api } from "../api/client";
import { Shield, Check, ChevronRight } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const lakh = (v) => {
    const x = Number(v || 0);
    if (x >= 10000000) return `${(x / 10000000).toFixed(0)}Cr`;
    if (x >= 100000) return `${(x / 100000).toFixed(0)}L`;
    return inr(x);
};

const TYPE_OPTIONS = [
    { value: "", label: "All plans" },
    { value: "individual", label: "Individual" },
    { value: "family", label: "Family" },
    { value: "senior", label: "Senior" },
    { value: "diabetic", label: "Diabetic" },
];

const Insurance = () => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/insurance-plans", { params: filter ? { popular_for: filter } : {} })
            .then((r) => setItems(r.data.items || []))
            .finally(() => setLoading(false));
    }, [filter]);

    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
            <Navbar />
            <main className="mx-auto max-w-6xl px-6 py-12 lg:px-12" data-testid="insurance-page">
                <p className="label-uppercase text-zinc-500">Health cover</p>
                <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-50" style={{ letterSpacing: "-0.02em" }}>Protect your family</h1>
                <p className="mt-2 max-w-xl text-sm text-zinc-500">Curated health insurance plans. Submit a request, our advisors will reach out with a personalised quote.</p>

                <div className="mt-6 flex flex-wrap gap-1.5" data-testid="insurance-filter-chips">
                    {TYPE_OPTIONS.map((o) => (
                        <button key={o.value} onClick={() => setFilter(o.value)} className={`chip ${filter === o.value ? "chip-active" : ""}`} data-testid={`insurance-filter-${o.value || "all"}`}>
                            {o.label}
                        </button>
                    ))}
                </div>

                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="insurance-plans-grid">
                    {loading ? (
                        Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-56 rounded-2xl skeleton" />)
                    ) : items.length === 0 ? (
                        <p className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500 sm:col-span-2 lg:col-span-3">No plans match this filter.</p>
                    ) : items.map((p) => (
                        <Link key={p.id} to={`/insurance/${p.slug}`} className="group relative flex flex-col rounded-2xl border border-zinc-100 bg-white p-5 transition-shadow hover:shadow-md dark:bg-white/[0.03] dark:border-white/10" data-testid={`insurance-plan-${p.slug}`}>
                            <div className="flex items-start justify-between gap-2">
                                <Shield className="h-5 w-5 text-brand-700" />
                                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-brand-700 capitalize">{p.popular_for}</span>
                            </div>
                            <p className="mt-3 text-xs uppercase tracking-wider text-zinc-500">{p.provider_name}</p>
                            <p className="font-medium text-zinc-900 dark:text-zinc-50 line-clamp-2">{p.plan_name}</p>
                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <p className="text-zinc-500">Cover</p>
                                    <p className="font-semibold text-zinc-900 dark:text-zinc-50">{lakh(p.cover_amount)}</p>
                                </div>
                                <div>
                                    <p className="text-zinc-500">Premium</p>
                                    <p className="font-semibold text-zinc-900 dark:text-zinc-50">{inr(p.premium_yearly)}<span className="text-[10px] text-zinc-500">/yr</span></p>
                                </div>
                            </div>
                            <ul className="mt-3 space-y-1 text-[11px] text-zinc-600 dark:text-zinc-400">
                                {(p.key_benefits || []).slice(0, 2).map((b, i) => (
                                    <li key={i} className="flex items-start gap-1"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />{b}</li>
                                ))}
                            </ul>
                            <p className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-brand-700 group-hover:underline">Get quote <ChevronRight className="h-3 w-3" /></p>
                        </Link>
                    ))}
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Insurance;
